import type { SignInReturnHandler } from "src/infrastructure/auth";

/**
 * Whether a router path stays in this app. A browser reads `//host` and
 * `/\host` as another site, after dropping tabs and newlines, so only a path
 * whose first segment is a real path character is accepted.
 */
function isInAppPath(path: string): boolean {
  const normalized = path.replace(/[\t\n\r]/g, "");
  return /^\/(?![/\\])/.test(normalized);
}

/**
 * Turns the browser path sign-in started from (it includes any deploy base
 * path, e.g. `/app/settings`) into the path the router navigates to, which is
 * relative to its `basename` (`/settings`). A path outside the base path is
 * returned unchanged. Anything that would leave the app (`//host`, `/\host`,
 * an absolute URL) becomes `/`.
 */
export function toRouterPath(browserPath: string, basePath: string): string {
  const path = stripBasePath(browserPath, basePath);
  return isInAppPath(path) ? path : "/";
}

function stripBasePath(browserPath: string, basePath: string): string {
  if (!browserPath) return "/";
  if (!basePath || !browserPath.startsWith(basePath)) return browserPath;

  const rest = browserPath.slice(basePath.length);
  if (rest === "") return "/";
  if (rest.startsWith("/")) return rest;
  if (rest.startsWith("?") || rest.startsWith("#")) return `/${rest}`;
  // e.g. "/application" under base "/app": a different path, not ours to strip.
  return browserPath;
}

/**
 * After Auth0's callback, go back to the page sign-in started from (else the
 * home page) through `navigate`, the router's, so it leaves the callback route.
 */
export function makeReturnAfterSignIn(
  navigate: (to: string, options: { replace: boolean }) => unknown,
  basePath: string
): SignInReturnHandler {
  return (returnTo) => void navigate(toRouterPath(returnTo ?? "/", basePath), { replace: true });
}
